import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import type {
  CreateCustomerFn,
  CreateMemberManuallyFn,
  FetchMemberFn,
  ListUserWorkspaceMembershipsFn,
  FetchWorkspaceFn,
  GetCurrentUserFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetUserDataFn,
  SearchMembersFn,
} from '@/features/iam'
import type {
  EmailCheckoutLinkFn,
  GenerateCheckoutLinkFn,
  InvalidateCheckoutSessionsFn,
} from '@/features/billing'
import type {
  ApplyJobCouponByCodeFn,
  FetchJobFn,
  PublishedLegendFn,
  JobCheckoutViewModelFactory,
  JobCreationRootViewModelFactory,
  RemoveJobCouponByCodeFn,
  SearchJobsFn,
  SubmitJobRequestFn,
} from '@/features/jobs'
import type { SearchCallCenterContactsFn } from '@/features/freshbooks'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'

/**
 * Options for {@link CallCenterFeature}.
 */
export interface CallCenterFeatureOptions {
  navigate: NavigateFn
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  searchCallCenterContacts: SearchCallCenterContactsFn
  searchMembers: SearchMembersFn
  fetchMember: FetchMemberFn
  createMemberManually: CreateMemberManuallyFn
  createCustomer: CreateCustomerFn
  getCurrentUser: GetCurrentUserFn
  getUserData: GetUserDataFn
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  listUserWorkspaceMemberships: ListUserWorkspaceMembershipsFn
  fetchWorkspace: FetchWorkspaceFn
  generateCheckoutLink: GenerateCheckoutLinkFn
  emailCheckoutLink: EmailCheckoutLinkFn
  invalidateCheckoutSessions: InvalidateCheckoutSessionsFn
  fetchJob: FetchJobFn
  searchJobs: SearchJobsFn
  submitJobRequest: SubmitJobRequestFn
  applyJobCouponByCode: ApplyJobCouponByCodeFn
  removeJobCouponByCode: RemoveJobCouponByCodeFn
  publishedLegend: PublishedLegendFn
  createJobCheckoutViewModel: PromiseLike<JobCheckoutViewModelFactory>
  createJobCreationRootViewModel: PromiseLike<JobCreationRootViewModelFactory>
}

/**
 * Call center for admins and call agents to look up customers and start jobs
 * on their behalf.
 */
export type CallCenterFeature = ReturnType<typeof CallCenterFeature>

export function CallCenterFeature(options: CallCenterFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalCallCenterFeature(options),
  )

  return {
    // Internal
    internal,
  }
}
